/**
 * AI Score Calculators - Calculate assessment quality metrics
 * Provides AI-driven insights into risk severity, mitigation effectiveness, and security posture
 */

/**
 * Calculate Risk Severity Score
 * Weighted aggregate of risk priorities to determine overall risk exposure
 *
 * @param {Object} priorityCounts - Risk counts by priority { Critical, High, Significant, Moderate, Low, Negligible }
 * @returns {Object} { score: number (0-100), level: string, explanation: string }
 */
export function calculateRiskSeverityScore(priorityCounts) {
  const { Critical = 0, High = 0, Significant = 0, Moderate = 0, Low = 0, Negligible = 0 } = priorityCounts;

  const totalRisks = Critical + High + Significant + Moderate + Low + Negligible;

  if (totalRisks === 0) {
    return {
      score: 0,
      level: 'NONE',
      explanation: 'No risks identified in the assessment.'
    };
  }

  // Weighted scoring: Critical (40%), High (30%), Significant (20%), Others (10%)
  const criticalWeight = (Critical / totalRisks) * 40;
  const highWeight = (High / totalRisks) * 30;
  const significantWeight = (Significant / totalRisks) * 20;
  const otherWeight = ((Moderate + Low + Negligible) / totalRisks) * 10;

  const score = Math.round(criticalWeight + highWeight + significantWeight + otherWeight);

  // Determine level based on score thresholds
  let level;
  if (score >= 70) level = 'HIGH';
  else if (score >= 40) level = 'MEDIUM';
  else level = 'LOW';

  // Generate detailed explanation
  const explanation = `AI analyzed ${totalRisks} total risks across all priority levels. ` +
    `The weighted severity calculation assigns the highest weight to Critical risks (${Critical}) ` +
    `and High risks (${High}), resulting in an overall severity score of ${score}/100, ` +
    `classified as ${level} risk exposure.`;

  return { score, level, explanation };
}

/**
 * Calculate Mitigation Quality Score
 * Measures effectiveness of planned controls and residual risk reduction
 *
 * @param {Object} savedControls - Controls by risk ID
 * @param {Object} residualRiskData - Residual risk assessments by risk ID
 * @param {Object} riskFormData - All risks data
 * @returns {Object} { score: number (0-100), level: string, explanation: string }
 */
export function calculateMitigationQualityScore(savedControls, residualRiskData, riskFormData) {
  // Count total risks across all areas
  let totalRisks = 0;
  let risksWithControls = 0;
  let totalInherentScore = 0;
  let totalResidualScore = 0;
  let risksWithResidual = 0;

  Object.values(riskFormData || {}).forEach(areaData => {
    Object.values(areaData.areaRisks || {}).forEach(risks => {
      if (Array.isArray(risks)) {
        risks.forEach(risk => {
          if (risk.status === 'Saved') {
            totalRisks++;

            // Check if risk has controls
            const controls = savedControls?.[risk.riskId] || [];
            if (controls.length > 0) {
              risksWithControls++;
            }

            // Check if risk has residual assessment
            const residual = residualRiskData?.[risk.riskId];
            if (residual) {
              risksWithResidual++;
              // Score inherent and residual (assuming higher is worse, convert to 0-100 scale)
              const inherentScore = getPriorityScore(risk.priority);
              const residualScore = getPriorityScore(residual.residualPriority);
              totalInherentScore += inherentScore;
              totalResidualScore += residualScore;
            }
          }
        });
      }
    });
  });

  if (totalRisks === 0) {
    return {
      score: 0,
      level: 'NONE',
      explanation: 'No risks to evaluate for mitigation quality.'
    };
  }

  // Calculate coverage: percentage of risks with controls
  const coverage = (risksWithControls / totalRisks) * 100;

  // Calculate reduction: average risk reduction from inherent to residual
  let reduction = 0;
  if (risksWithResidual > 0) {
    const avgInherent = totalInherentScore / risksWithResidual;
    const avgResidual = totalResidualScore / risksWithResidual;
    reduction = ((avgInherent - avgResidual) / avgInherent) * 100;
  }

  // Weighted score: 60% coverage, 40% reduction
  const score = Math.round((coverage * 0.6) + (reduction * 0.4));

  // Determine effectiveness level
  let level;
  if (score >= 75) level = 'EFFECTIVE';
  else if (score >= 50) level = 'MODERATE';
  else level = 'WEAK';

  const explanation = `AI assessed ${totalRisks} risks: ${risksWithControls} have mitigation controls (${Math.round(coverage)}% coverage). ` +
    `Of ${risksWithResidual} risks with residual assessments, controls achieved an average ${Math.round(reduction)}% risk reduction. ` +
    `Overall mitigation quality: ${score}/100, rated as ${level}.`;

  return { score, level, explanation };
}

/**
 * Calculate Security Threat Level
 * Aggregates threat assessments from security scenarios
 *
 * @param {Object} securityState - Security scenarios with threat assessments
 * @returns {Object} { score: number (0-100), level: string, explanation: string }
 */
export function calculateSecurityThreatLevel(securityState) {
  const scenarios = securityState?.scenarios || {};
  const scenarioList = Object.values(scenarios);

  if (scenarioList.length === 0) {
    return {
      score: 0,
      level: 'NONE',
      explanation: 'No security threats identified or assessed yet.'
    };
  }

  // Count threats by level
  const threatCounts = {
    Critical: 0,
    High: 0,
    Moderate: 0,
    Low: 0,
    Unknown: 0
  };

  scenarioList.forEach(scenario => {
    const threatAssessment = scenario.assessmentData?.['Threat Assessment'];
    const threatLevel = threatAssessment?.threatLevel || 'Unknown';
    const normalizedLevel = threatLevel.charAt(0).toUpperCase() + threatLevel.slice(1).toLowerCase();

    if (threatCounts.hasOwnProperty(normalizedLevel)) {
      threatCounts[normalizedLevel]++;
    } else {
      threatCounts.Unknown++;
    }
  });

  // Weighted threat scoring
  const threatScore =
    (threatCounts.Critical * 4) +
    (threatCounts.High * 3) +
    (threatCounts.Moderate * 2) +
    (threatCounts.Low * 1);

  const maxPossible = scenarioList.length * 4; // All Critical would be max
  const normalized = Math.round((threatScore / maxPossible) * 100);

  // Determine threat level
  let level;
  if (normalized >= 75) level = 'CRITICAL';
  else if (normalized >= 50) level = 'HIGH';
  else if (normalized >= 25) level = 'MEDIUM';
  else level = 'LOW';

  const explanation = `AI analyzed ${scenarioList.length} security scenarios: ` +
    `${threatCounts.Critical} Critical, ${threatCounts.High} High, ${threatCounts.Moderate} Moderate, ${threatCounts.Low} Low threats. ` +
    `Weighted threat score: ${normalized}/100, classified as ${level} security posture.`;

  return {
    score: normalized,
    level,
    explanation
  };
}

/**
 * Helper: Convert priority to numeric score (0-100)
 */
function getPriorityScore(priority) {
  const scores = {
    Critical: 100,
    High: 80,
    Significant: 60,
    Moderate: 40,
    Low: 20,
    Negligible: 10,
    None: 0
  };

  const normalized = priority?.charAt(0).toUpperCase() + priority?.slice(1).toLowerCase();
  return scores[normalized] || 0;
}
